/**
 * useOfflineBundle — pre-cache a match for truly-offline scoring.
 *
 *  - downloadForOffline(matchId): hits /api/matches/<id>/offline-bundle/, stores in IDB
 *  - getCachedBundle(matchId): pulls back from IDB
 *  - All caches are versioned by cached_at timestamp
 */
import { useCallback, useEffect, useState } from "react";
import api from "../api/axios";

const DB    = "zcd_offline_bundles_v1";
const STORE = "bundles";

function dbOpen() {
  return new Promise((resolve, reject) => {
    const r = indexedDB.open(DB, 1);
    r.onupgradeneeded = () => r.result.createObjectStore(STORE, { keyPath: "match_id" });
    r.onsuccess = () => resolve(r.result);
    r.onerror   = () => reject(r.error);
  });
}
async function idbPut(row) {
  const db = await dbOpen();
  const tx = db.transaction(STORE, "readwrite");
  tx.objectStore(STORE).put(row);
  return new Promise((r) => (tx.oncomplete = r));
}
async function idbGet(matchId) {
  const db = await dbOpen();
  return new Promise((res) => {
    const tx = db.transaction(STORE, "readonly");
    const r = tx.objectStore(STORE).get(matchId);
    r.onsuccess = () => res(r.result || null);
    r.onerror   = () => res(null);
  });
}
async function idbDelete(matchId) {
  const db = await dbOpen();
  const tx = db.transaction(STORE, "readwrite");
  tx.objectStore(STORE).delete(matchId);
  return new Promise((r) => (tx.oncomplete = r));
}

export async function downloadForOffline(matchId) {
  const res = await api.get(`/api/matches/${matchId}/offline-bundle/`);
  const data = res.data;
  data.match_id = matchId;
  data.cached_at = new Date().toISOString();
  await idbPut(data);
  return data;
}

export async function getCachedBundle(matchId) {
  return idbGet(matchId);
}

export async function clearCachedBundle(matchId) {
  return idbDelete(matchId);
}

export function useOfflineBundle(matchId) {
  const [cached, setCached] = useState(null);
  const [busy, setBusy]     = useState(false);
  const [err, setErr]       = useState("");

  const refresh = useCallback(async () => {
    if (!matchId) return;
    const c = await idbGet(matchId);
    setCached(c);
  }, [matchId]);
  useEffect(() => { refresh(); }, [refresh]);

  const download = useCallback(async () => {
    if (!matchId) return;
    setBusy(true); setErr("");
    try {
      const data = await downloadForOffline(matchId);
      setCached(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Download failed");
    } finally {
      setBusy(false);
    }
  }, [matchId]);

  const clear = useCallback(async () => {
    if (!matchId) return;
    await idbDelete(matchId);
    setCached(null);
  }, [matchId]);

  return { cached, busy, err, download, clear, refresh };
}
